import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { publicApi, AvailablePlan } from "../services/api";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import AppLoader from "./AppLoader";
import "./PricingPage.css";

type BillingCycle = "monthly" | "yearly";

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  
  // State
  const [plans, setPlans] = useState<AvailablePlan[]>([]);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [trialPeriodDays, setTrialPeriodDays] = useState(0);

  // Load plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoading(true);
        setError("");
        const response = await publicApi.getPlans();
        setPlans(response.plans);
        setTrialPeriodDays(response.trial_period_days || 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load plans");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPlans();
  }, []);

  // Format price
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCentsPrice = (cents: number) => {
    return formatPrice(cents / 100);
  };

  // Get plan price based on billing cycle
  const getPlanPrice = (plan: AvailablePlan): string => {
    if (billingCycle === "monthly") {
      if (plan.stripe_monthly_price) {
        return formatCentsPrice(plan.stripe_monthly_price.amount);
      }
      return formatPrice(plan.price);
    } else {
      if (plan.stripe_yearly_price) {
        return formatCentsPrice(plan.stripe_yearly_price.amount);
      }
      return formatPrice(plan.yearly_price);
    }
  };

  // Check if plan has pricing for selected cycle
  const hasPriceForCycle = (plan: AvailablePlan): boolean => {
    if (billingCycle === "monthly") {
      return !!(plan.stripe_monthly_price || plan.price > 0);
    }
    return !!(plan.stripe_yearly_price || plan.yearly_price > 0);
  };

  // Calculate yearly savings
  const getYearlySavings = (plan: AvailablePlan): string | null => {
    const monthlyAmount = plan.stripe_monthly_price?.amount || (plan.price * 100);
    const yearlyAmount = plan.stripe_yearly_price?.amount || (plan.yearly_price * 100);
    
    if (!monthlyAmount || !yearlyAmount || yearlyAmount <= 0) return null;
    
    const annualMonthly = monthlyAmount * 12;
    const savings = annualMonthly - yearlyAmount;
    
    if (savings <= 0) return null;
    
    const savingsPercent = Math.round((savings / annualMonthly) * 100);
    return `Save ${savingsPercent}%`;
  };

  // Handle "Get Started" click
  const handleGetStarted = (planId: string) => {
    navigate(`/signup?plan=${planId}`);
  };

  // Loading state
  if (isLoading) {
    return <AppLoader message="Loading plans..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="pricing-page">
        <div className="pricing-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
        </div>
        <div className="pricing-error">
          <WarningIcon className="error-icon" />
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <div className="pricing-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
      </div>

      <div className="pricing-container">
        {/* Header */}
        <header className="pricing-header">
          <Link to="/login" className="pricing-logo-link">
            <div className="pricing-logo">
              <img src="/clusterview-logo.svg" alt="ClusterView" className="logo-icon" style={{ width: 28, height: 28 }} />
            </div>
            <span>ClusterView</span>
          </Link>
        </header>

        {/* Hero Section */}
        <section className="pricing-hero">
          <h1>Simple, Transparent Pricing</h1>
          <p>
            Choose the plan that fits your needs.
            {trialPeriodDays > 0
              ? ` Start with a ${trialPeriodDays}-day free trial — no charge until your trial ends.`
              : ` All plans include a ${trialPeriodDays}-day money-back guarantee.`}
          </p>
        </section>

        {/* Billing Cycle Toggle */}
        {/* <div className="billing-toggle-wrapper">
          <div className="billing-toggle">
            <button
              className={`toggle-btn ${billingCycle === "monthly" ? "active" : ""}`}
              onClick={() => setBillingCycle("monthly")}
            >
              Monthly
            </button>
            <button
              className={`toggle-btn ${billingCycle === "yearly" ? "active" : ""}`}
              onClick={() => setBillingCycle("yearly")}
            >
              Yearly
              <span className="toggle-badge">Save up to 20%</span>
            </button>
          </div>
        </div> */}

        {/* Plans Grid */}
        <div className="pricing-grid mt-12">
          {plans.map((plan, index) => {
            const yearlySavings = billingCycle === "yearly" ? getYearlySavings(plan) : null;
            const hasPrice = hasPriceForCycle(plan);
            const isPopular = index === plans.length - 1 && plans.length > 1;
            
            return (
              <div
                key={plan.id}
                className={`pricing-card ${isPopular ? "popular" : ""} ${!hasPrice ? "disabled" : ""}`}
              >
                {isPopular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                
                {yearlySavings && (
                  <div className="savings-badge">{yearlySavings}</div>
                )}

                <div className="card-header">
                  <h2 className="plan-name">{plan.name}</h2>
                  <div className="plan-price">
                    <span className="price-amount">{getPlanPrice(plan)}</span>
                    <span className="price-period">
                      /{billingCycle === "monthly" ? "month" : "year"}
                    </span>
                  </div>
                  {billingCycle === "yearly" && plan.stripe_yearly_price && (
                    <p className="monthly-equiv">
                      {formatCentsPrice(plan.stripe_yearly_price.amount / 12)}/month billed annually
                    </p>
                  )}
                </div>

                <ul className="feature-list">
                  <li>
                    <CheckIcon />
                    <span><strong>{plan.max_domains}</strong> Domain{plan.max_domains !== 1 ? "s" : ""}</span>
                  </li>
                  <li>
                    <CheckIcon />
                    <span><strong>{plan.max_clusters}</strong> Keyword Clusters</span>
                  </li>
                  <li>
                    <CheckIcon />
                    <span><strong>{plan.max_keywords_per_cluster}</strong> Keywords per Cluster</span>
                  </li>
                  <li>
                    <CheckIcon />
                    <span><strong>{plan.max_users}</strong> Team Member{plan.max_users !== 1 ? "s" : ""}</span>
                  </li>
                  <li>
                    <CheckIcon />
                    <span>SERP Preview & Tracking</span>
                  </li>
                  <li>
                    <CheckIcon />
                    <span>AI Topic Suggestions</span>
                  </li>
                  <li>
                    <CheckIcon />
                    <span>Email Support</span>
                  </li>
                </ul>

                <button
                  className={`cta-btn ${isPopular ? "popular" : ""}`}
                  onClick={() => handleGetStarted(plan.id)}
                  disabled={!hasPrice}
                >
                  {!hasPrice
                    ? "Not Available"
                    : trialPeriodDays > 0
                    ? `Start ${trialPeriodDays}-Day Free Trial`
                    : "Get Started"}
                </button>
              </div>
            );
          })}

          {/* Enterprise Plan - Contact Sales */}
          <div className="pricing-card enterprise">
            <div className="enterprise-badge">Enterprise</div>

            <div className="card-header">
              <h2 className="plan-name">Enterprise</h2>
              <div className="plan-price">
                <span className="price-amount">Custom</span>
              </div>
            </div>

            <ul className="feature-list">
              <li>
                <CheckIcon />
                <span><strong>Unlimited</strong> Domains</span>
              </li>
              <li>
                <CheckIcon />
                <span><strong>Unlimited</strong> Keyword Clusters</span>
              </li>
              <li>
                <CheckIcon />
                <span><strong>Unlimited</strong> Keywords per Cluster</span>
              </li>
              <li>
                <CheckIcon />
                <span><strong>Unlimited</strong> Team Members</span>
              </li>
              <li>
                <CheckIcon />
                <span>SERP Preview & Tracking</span>
              </li>
              <li>
                <CheckIcon />
                <span>AI Topic Suggestions</span>
              </li>
              <li>
                <CheckIcon />
                <span>Dedicated Account Manager</span>
              </li>
              <li>
                <CheckIcon />
                <span>Priority Support & SLA</span>
              </li>
            </ul>

            <a
              className="cta-btn enterprise"
              href='https://clusterview.ai/contact'
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Us
            </a>
          </div>
        </div>

        {/* FAQ or Additional Info */}
        <section className="pricing-info">
          <div className="info-card">
            <h3>All plans include:</h3>
            <ul>
              {trialPeriodDays > 0 && (
                <li>{trialPeriodDays}-day free trial</li>
              )}
              <li>Credit card required{trialPeriodDays > 0 ? " — not charged until trial ends" : ""}</li>
              <li>Secure payment via Stripe</li>
              <li>Cancel anytime</li>
              <li>Automatic updates</li>
            </ul>
          </div>
        </section>

        {/* Footer */}
        <footer className="pricing-footer">
          <p>
            Already have an account? <Link to="/login">Sign In</Link>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PricingPage;
